/**
 * "Is there a connection right now", for the offline queue.
 *
 * navigator.onLine alone is optimistic in a WebView (it can say online on a
 * captive or dead Wi-Fi), so when the Capacitor shell reports a native status
 * (capacitor/offline.js forwards @capacitor/network events here) that wins.
 * A wrong "online" costs one failed request that is then queued anyway; a wrong
 * "offline" only delays a sync, so neither direction can lose data.
 */

let nativeConnected = null; // null until the shell has reported
const listeners = new Set();

export const isOnline = () => {
  if (nativeConnected !== null) return nativeConnected;
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
};

const notify = () => {
  const online = isOnline();
  listeners.forEach((listener) => {
    try {
      listener(online);
    } catch {
      // one bad listener must not stop the others
    }
  });
};

/** Called by capacitor/offline.js with @capacitor/network's `connected`. */
export const setNativeConnectivity = (connected) => {
  const next = Boolean(connected);
  if (next === nativeConnected) return;
  nativeConnected = next;
  notify();
};

let windowEventsBound = false;
const bindWindowEvents = () => {
  if (windowEventsBound || typeof window === "undefined") return;
  windowEventsBound = true;
  window.addEventListener("online", notify);
  window.addEventListener("offline", notify);
};

/** Subscribe to connectivity changes. Returns an unsubscribe function. */
export const onConnectivityChange = (listener) => {
  bindWindowEvents();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const resetConnectivityForTests = () => {
  nativeConnected = null;
  listeners.clear();
};
